import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Package, Plus, Search, Filter, Download, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, AlertTriangle,
  Database, Play, Save, Loader2, ClipboardCheck, RotateCcw, Warehouse
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  draft: { label: 'پیش‌نویس', variant: 'neutral' as const, icon: ClipboardCheck },
  in_progress: { label: 'در حال انجام', variant: 'info' as const, icon: Play },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: CheckCircle },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function CycleCountsPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<CycleCountListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.cycleCounts(filters),
    queryFn: () => inventoryApi.listCycleCounts(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => inventoryApi.createCycleCount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cycleCounts() })
      toast.success('شمارش دوره‌ای ایجاد شد')
      closeModal('create-cycle-count')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const startMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.startCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cycleCounts() })
      toast.success('شمارش آغاز شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'آغاز ناموفق بود'),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.completeCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cycleCounts() })
      toast.success('شمارش تکمیل شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'تکمیل ناموفق بود'),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.cancelCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cycleCounts() })
      toast.success('شمارش لغو شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'لغو ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.deleteCycleCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cycleCounts() })
      toast.success('شمارش حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (cycleCount: any) => [
    { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/inventory/cycle-counts/${cycleCount.id}`, '_blank') },
    { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('stock', 'count') || cycleCount.status !== 'draft' },
    { type: 'divider' },
    { label: 'شروع شمارش', icon: Play, onClick: () => startMutation.mutate(cycleCount.id), disabled: !hasPermission('stock', 'count') || cycleCount.status !== 'draft' },
    { label: 'تکمیل شمارش', icon: CheckCircle, onClick: () => completeMutation.mutate(cycleCount.id), disabled: !hasPermission('stock', 'count') || cycleCount.status !== 'in_progress' },
    { label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(cycleCount.id), disabled: !hasPermission('stock', 'count') || cycleCount.status === 'completed' || cycleCount.status === 'cancelled' },
    { type: 'divider' },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(cycleCount.id); openModal('delete-confirm') }, disabled: !hasPermission('stock', 'count') || cycleCount.status !== 'draft' },
  ]

  const getProgress = (cycleCount: any) => {
    if (!cycleCount.lines?.length) return 0
    const counted = cycleCount.lines.filter((l: any) => l.counted_quantity !== null && l.counted_quantity !== undefined).length
    return Math.round((counted / cycleCount.lines.length) * 100)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">شمارش دوره‌ای</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} شمارش ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-cycle-count')} disabled={!hasPermission('stock', 'count')}>
            <Plus className="w-4 h-4" />
            شمارش جدید
          </Button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره شمارش، انبار، یادداشت)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'draft', label: 'پیش‌نویس' },
              { value: 'in_progress', label: 'در حال انجام' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-40"
          />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره شمارش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار</th>
                    <th>آیتم‌ها</th>
                    <th>پیشرفت</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="60px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری شمارش‌ها</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardCheck className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ شمارش دوره‌ای یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین شمارش را ایجاد کنید</p>
            <Button onClick={() => openModal('create-cycle-count')} disabled={!hasPermission('stock', 'count')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد شمارش
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره شمارش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">انبار</th>
                    <th>آیتم‌ها</th>
                    <th>پیشرفت</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((cycleCount, index) => {
                    const statusInfo = statusConfig[cycleCount.status as keyof typeof statusConfig] || statusConfig.draft
                    const progress = getProgress(cycleCount)
                    return (
                      <motion.tr
                        key={cycleCount.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{cycleCount.cycle_count_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(cycleCount.created_at)}</td>
                        <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{cycleCount.warehouse?.name || '—'}</td>
                        <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{cycleCount.lines?.length || 0}</td>
                        <td>
                          <div className="flex items-center gap-3 min-w-[150px]">
                            <div className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                              <div className={cn(
                                'h-full rounded-full transition-all duration-500',
                                cycleCount.status === 'completed' ? 'bg-success-500' :
                                cycleCount.status === 'in_progress' ? 'bg-primary-500' :
                                cycleCount.status === 'cancelled' ? 'bg-danger-500' :
                                'bg-neutral-400'
                              )} style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-sm font-mono text-neutral-600 dark:text-neutral-400 w-10 text-left">{progress}%</span>
                          </div>
                        </td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <statusInfo.icon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(cycleCount)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Cycle Count Modal */}
      <Modal
        isOpen={modals['create-cycle-count']}
        onClose={() => closeModal('create-cycle-count')}
        title="ایجاد شمارش دوره‌ای جدید"
        size="md"
      >
        <CycleCountForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-cycle-count')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف شمارش دوره‌ای"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این شمارش دوره‌ای مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const cycleCountFormSchema = z.object({
  warehouse_id: z.string().uuid('انبار معتبر نیست'),
  notes: z.string().optional(),
})

type CycleCountFormData = z.infer<typeof cycleCountFormSchema>

interface CycleCountFormProps {
  onSubmit: (data: CycleCountFormData) => void
  onClose: () => void
  isLoading: boolean
}

function CycleCountForm({ onSubmit, onClose, isLoading }: CycleCountFormProps) {
  const methods = useForm<CycleCountFormData>({
    resolver: zodResolver(cycleCountFormSchema),
    defaultValues: {
      warehouse_id: '',
      notes: '',
    },
  })

  const { control, handleSubmit } = methods

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="warehouse_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="انبار *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="انبار" {...field} />
          )}
        />
      </div>

      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">یادداشت‌ها</label>
            <textarea
              {...field}
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
              placeholder="یادداشت‌های مربوط به این شمارش..."
            />
          </div>
        )}
      />

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <ClipboardCheck className="w-4 h-4 ml-1" /> ایجاد شمارش </> }
        </Button>
      </div>
    </form>
  )
}

type CycleCountListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  warehouse_id?: string
  status?: string
  date_from?: string
  date_to?: string
}